import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, ArrowLeft, ArrowRight, Home, ChevronRight, AlertCircle, Loader2 } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import WizardStepper from './components/WizardStepper';
import UploadMethodStep from './components/UploadMethodStep';
import FileUploadStep from './components/FileUploadStep';
import MapDocumentsStep from './components/MapDocumentsStep';
import OcrReviewStep from './components/OcrReviewStep';
import ValidationSummaryStep from './components/ValidationSummaryStep';
import ProcessingStep from './components/ProcessingStep';
import {
  BulkUploadMethod,
  BulkUploadJobView,
  BulkUploadJobStatus,
  BulkUploadDocumentStatus,
  BulkUploadValidationSummaryView,
  MapDocumentInput,
  createBulkUploadJob,
  uploadBulkFiles,
  removeBulkUploadDocument,
  getBulkUploadJob,
  mapBulkUploadDocuments,
  validateBulkUploadJob,
  processBulkUploadJob,
  runBulkUploadOcr,
} from '../../../api/General/bulkUpload';
import { apiErrorsOf } from '../../../utils/requestHandling';

function getWizardSteps(isOcr: boolean) {
  return [
    { label: 'Upload Method', description: 'OCR or Manual' },
    { label: 'File Upload', description: 'Add documents' },
    isOcr
      ? { label: 'OCR Review', description: 'Review detections' }
      : { label: 'Map Documents', description: 'Assign employees' },
    { label: 'Validation', description: 'Review & verify' },
    { label: 'Processing', description: 'Upload progress' },
  ];
}

/** Determine which wizard step a resumed job should land on. */
function getStepForJob(job: BulkUploadJobView): number {
  if (job.status === BulkUploadJobStatus.Processing ||
      job.status === BulkUploadJobStatus.Completed ||
      job.status === BulkUploadJobStatus.Failed) {
    return 4;
  }
  if (job.status === BulkUploadJobStatus.OcrProcessing) {
    return 2;
  }
  const docs = job.documents ?? [];
  if (docs.length === 0) return 1;
  const hasOcrDocs = docs.some(
    (d) => d.status === BulkUploadDocumentStatus.OcrCompleted ||
           d.status === BulkUploadDocumentStatus.OcrProcessing ||
           d.status === BulkUploadDocumentStatus.Mapped
  );
  if (hasOcrDocs) return 2;
  return 1;
}

export default function BulkUploadPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [method, setMethod] = useState<BulkUploadMethod | null>(null);
  const [job, setJob] = useState<BulkUploadJobView | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mappings, setMappings] = useState<
    Record<number, { userId: string; companyAttachmentTypeId: number; expiryDate?: string; reminderDays?: number }>
  >({});
  const [validation, setValidation] = useState<BulkUploadValidationSummaryView | null>(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // OCR-specific state
  const [ocrRunning, setOcrRunning] = useState(false);
  const [ocrError, setOcrError] = useState<string | null>(null);
  const [ocrFallbackToManual, setOcrFallbackToManual] = useState(false);
  const ocrTriggered = useRef(false);
  const resumeLoaded = useRef(false);

  const isOcrMode = method === BulkUploadMethod.OCR && !ocrFallbackToManual;
  const wizardSteps = getWizardSteps(isOcrMode);
  const documents = job?.documents ?? [];

  // ───────────────────────────────────────────────────
  // Resume from URL ?jobId=X on initial mount only
  // ───────────────────────────────────────────────────
  useEffect(() => {
    const jobId = new URLSearchParams(window.location.search).get('jobId');
    if (!jobId) {
      resumeLoaded.current = true; // no job to resume — mark so we don't interfere later
      return;
    }

    resumeLoaded.current = true;
    (async () => {
      try {
        const result = await getBulkUploadJob(Number(jobId));
        if (result.errors?.length > 0) return;
        const resumedJob = result.payload;
        setJob(resumedJob);
        setMethod(resumedJob.method);
        setCurrentStep(getStepForJob(resumedJob));

        // If OCR was in progress, resume polling
        if (resumedJob.status === BulkUploadJobStatus.OcrProcessing) {
          ocrTriggered.current = true;
          setOcrRunning(true);
        }
      } catch {
        // Job not found — start fresh
      }
    })();
  }, []);  

  // ───────────────────────────────────────────────────
  // Persist jobId in URL when job is created
  // ───────────────────────────────────────────────────
  useEffect(() => {
    if (job && !searchParams.get('jobId')) {
      setSearchParams({ jobId: String(job.id) }, { replace: true });
    }
  }, [job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ───────────────────────────────────────────────────
  // Polling for processing progress (Step 4)
  // ───────────────────────────────────────────────────
  useEffect(() => {
    if (
      currentStep === 4 &&
      job &&
      job.status === BulkUploadJobStatus.Processing
    ) {
      pollingRef.current = setInterval(async () => {
        try {
          const result = await getBulkUploadJob(job.id);
          if (result.errors?.length === 0) {
            setJob(result.payload);
            if (
              result.payload.status === BulkUploadJobStatus.Completed ||
              result.payload.status === BulkUploadJobStatus.Failed
            ) {
              if (pollingRef.current) clearInterval(pollingRef.current);
            }
          }
        } catch {
          // silent - will retry on next poll
        }
      }, 1500);

      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
      };
    }
  }, [currentStep, job?.id, job?.status]);

  // ───────────────────────────────────────────────────
  // Trigger OCR and poll until ALL documents are done
  // ───────────────────────────────────────────────────
  useEffect(() => {
    if (currentStep !== 2 || !isOcrMode || !job) return;

    const allDocsFinished = (j: BulkUploadJobView) => {
      const docs = j.documents ?? [];
      return docs.length > 0 && docs.every(
        (d) => d.status === BulkUploadDocumentStatus.OcrCompleted ||
               d.status === BulkUploadDocumentStatus.Mapped
      );
    };

    // Trigger OCR if not already done
    if (!ocrTriggered.current) {
      ocrTriggered.current = true;
      setOcrRunning(true);
      setOcrError(null);

      (async () => {
        try {
          const result = await runBulkUploadOcr(job.id);
          if (result.errors?.length > 0) {
            const msg = result.errors[0]?.description ?? result.errors[0]?.message ?? t('OCR processing failed');
            setOcrError(msg);
            setOcrRunning(false);
            return;
          }
          setJob(result.payload);
        } catch {
          setOcrError(t('OCR service is currently unavailable'));
          setOcrRunning(false);
          return;
        }

        // Start polling — continue until every document is done
        pollingRef.current = setInterval(async () => {
          try {
            const pollResult = await getBulkUploadJob(job.id);
            if (pollResult.errors?.length === 0) {
              setJob(pollResult.payload);
              if (allDocsFinished(pollResult.payload)) {
                setOcrRunning(false);
                if (pollingRef.current) clearInterval(pollingRef.current);
              }
            }
          } catch {
            // silent - will retry
          }
        }, 2000);
      })();
    }

    // If resuming with OCR already running, just poll
    if (ocrTriggered.current && ocrRunning && !pollingRef.current) {
      pollingRef.current = setInterval(async () => {
        try {
          const pollResult = await getBulkUploadJob(job.id);
          if (pollResult.errors?.length === 0) {
            setJob(pollResult.payload);
            if (allDocsFinished(pollResult.payload)) {
              setOcrRunning(false);
              if (pollingRef.current) clearInterval(pollingRef.current);
            }
          }
        } catch {
          // silent
        }
      }, 2000);
    }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, [currentStep, isOcrMode, job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMethodSelect = useCallback((selectedMethod: BulkUploadMethod) => {
    setMethod(selectedMethod);
  }, []);

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      setError(null);
      setUploading(true);
      try {
        let currentJob = job;
        if (!currentJob) {
          if (method === null) return;
          const createResult = await createBulkUploadJob(method);
          if (createResult.errors?.length > 0) {
            setError(createResult.errors[0]?.description ?? t('Failed to create upload job'));
            return;
          }
          currentJob = createResult.payload;
          setJob(currentJob);
        }
        const uploadResult = await uploadBulkFiles(currentJob.id, files);
        if (uploadResult.errors?.length > 0) {
          setError(uploadResult.errors[0]?.description ?? t('Failed to upload files'));
          return;
        }
        setJob(uploadResult.payload);
      } catch (err) {
        // The shared request handler REJECTS every 4xx, so a server refusal (e.g. "The file content does not
        // match its extension.") lands here, not in the `errors` branch above. Show that reason; the generic
        // text is only for failures that carry none (network, timeout, empty body).
        const first = apiErrorsOf(err)[0];
        const reason = typeof first?.description === 'string' && first.description.trim() ? first.description : null;
        setError(reason ?? t('An unexpected error occurred during upload'));
      } finally {
        setUploading(false);
      }
    },
    [job, method, t]
  );

  const handleRemoveDocument = useCallback(
    async (documentId: number) => {
      if (!job) return;
      setError(null);
      try {
        const result = await removeBulkUploadDocument(documentId);
        if (result.errors?.length > 0) {
          setError(result.errors[0]?.description ?? t('Failed to remove document'));
          return;
        }
        const refreshed = await getBulkUploadJob(job.id);
        if (refreshed.errors?.length === 0) {
          setJob(refreshed.payload);
        }
      } catch {
        setError(t('An unexpected error occurred'));
      }
    },
    [job, t]
  );

  const handleSwitchToManual = useCallback(() => {
    setOcrFallbackToManual(true);
    setOcrError(null);
    setOcrRunning(false);
    if (pollingRef.current) clearInterval(pollingRef.current);
  }, []);

  const allDocumentsMapped = documents.length > 0 && documents.every(
    (d) => mappings[d.id]?.userId && mappings[d.id]?.companyAttachmentTypeId
  );

  const canGoNext = (): boolean => {
    if (currentStep === 0) return method !== null;
    if (currentStep === 1) return documents.length > 0 && !uploading;
    if (currentStep === 2) return allDocumentsMapped && !saving && !ocrRunning;
    if (currentStep === 3) return validation !== null && validation.errorCount === 0 && validation.validCount > 0;
    return false;
  };

  const handleNext = async () => {
    if (!canGoNext()) return;

    // Step 2 → Step 3: Save mappings to backend
    if (currentStep === 2 && job) {
      setError(null);
      setSaving(true);
      try {
        const mapInputs: MapDocumentInput[] = documents.map((d) => ({
          documentId: d.id,
          userId: mappings[d.id].userId,
          companyAttachmentTypeId: mappings[d.id].companyAttachmentTypeId,
          expiryDate: mappings[d.id].expiryDate || undefined,
          reminderDays: mappings[d.id].reminderDays || undefined,
        }));
        const result = await mapBulkUploadDocuments(job.id, mapInputs);
        if (result.errors?.length > 0) {
          setError(result.errors[0]?.description ?? result.errors[0]?.message ?? t('Failed to save mappings'));
          return;
        }
        setJob(result.payload);

        // Automatically validate
        setValidating(true);
        const valResult = await validateBulkUploadJob(job.id);
        if (valResult.errors?.length > 0) {
          setError(valResult.errors[0]?.description ?? valResult.errors[0]?.message ?? t('Validation failed'));
          setValidating(false);
          return;
        }
        setValidation(valResult.payload);
        setValidating(false);
        setCurrentStep(3);
      } catch {
        setError(t('An unexpected error occurred'));
      } finally {
        setSaving(false);
      }
      return;
    }

    // Step 3 → Step 4: Start processing
    if (currentStep === 3 && job) {
      setError(null);
      try {
        const result = await processBulkUploadJob(job.id);
        if (result.errors?.length > 0) {
          setError(result.errors[0]?.description ?? result.errors[0]?.message ?? t('Failed to start processing'));
          return;
        }
        setJob(result.payload);
        setCurrentStep(4);
      } catch {
        setError(t('An unexpected error occurred'));
      }
      return;
    }

    if (currentStep < wizardSteps.length - 1) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0 && currentStep < 4) {
      setError(null);
      if (currentStep === 2 && isOcrMode) {
        // Reset OCR state when going back from OCR review
        ocrTriggered.current = false;
        setOcrRunning(false);
        setOcrError(null);
        if (pollingRef.current) clearInterval(pollingRef.current);
      }
      setCurrentStep((s) => s - 1);
    }
  };

  const handleDone = () => {
    navigate(-1);
  };

  const getNextLabel = (): string => {
    if (currentStep === 2) return saving ? t('Saving...') : t('Save & Validate');
    if (currentStep === 3) return t('Start Processing');
    return t('Next');
  };

  return (
    <PageLayout
      title={t('Bulk Employee Documents Upload')}
      description={t('Upload and process multiple employee documents with OCR assistance')}
      icon={Upload}
      accentColor="amber"
      maxWidth="1600px"
    >
        {/* Wizard Card */}
        <div className="bg-background rounded-xl shadow-sm border border-border overflow-hidden">
          {/* Stepper */}
          <WizardStepper steps={wizardSteps} currentStep={currentStep} />

          {/* Error banner */}
          {error && (
            <div className="mx-6 mt-4 flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Step content */}
          <div className="min-h-[320px]">
            {currentStep === 0 && (
              <UploadMethodStep method={method} onMethodSelect={handleMethodSelect} />
            )}
            {currentStep === 1 && (
              <FileUploadStep
                uploadedDocuments={documents}
                uploading={uploading}
                onFilesSelected={handleFilesSelected}
                onRemoveDocument={handleRemoveDocument}
              />
            )}
            {currentStep === 2 && isOcrMode && (
              <OcrReviewStep
                documents={documents}
                ocrRunning={ocrRunning}
                ocrError={ocrError}
                mappings={mappings}
                onMappingsChange={setMappings}
                onSwitchToManual={handleSwitchToManual}
              />
            )}
            {currentStep === 2 && !isOcrMode && (
              <MapDocumentsStep
                documents={documents}
                mappings={mappings}
                onMappingsChange={setMappings}
              />
            )}
            {currentStep === 3 && (
              <ValidationSummaryStep
                validation={validation}
                loading={validating}
                error={null}
              />
            )}
            {currentStep === 4 && job && (
              <ProcessingStep job={job} onDone={handleDone} />
            )}
          </div>

          {/* Footer */}
          {currentStep < 4 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <button
                type="button"
                onClick={currentStep === 0 ? () => navigate(-1) : handleBack}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
              >
                {currentStep === 0 ? (
                  t('Cancel')
                ) : (
                  <>
                    <ArrowLeft className="w-4 h-4" />
                    {t('Back')}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={!canGoNext()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {getNextLabel()}
                {!saving && currentStep < 3 && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>
    </PageLayout>
  );
}
